import { useEffect, useRef, useState } from "react";
import { useNavigate, useRouterState } from "@tanstack/react-router";
import { ArrowLeft, Package, Plus } from "lucide-react";
import { AdminBreadcrumb } from "@/components/admin/AdminBreadcrumb";
import { AdminDrawer } from "@/components/admin/AdminDrawer";
import { AdminDrawerFooter } from "@/components/admin/AdminDrawerFooter";
import { AdminPageShell } from "@/components/admin/AdminPageShell";
import {
  adminAddButtonStyle,
  adminDrawerBodyStyle,
  adminFieldStyle,
  adminInputStyle,
  adminLabelStyle,
  adminSelectStyle,
} from "@/components/admin/adminFormStyles";
import {
  FloatingToast,
  type FloatingToastTone,
} from "@/components/feedback/FloatingToast";
import { OperationalFilterSurface } from "@/components/operational/OperationalFilterSurface";
import { OperationalPager } from "@/components/operational/OperationalPager";
import { OperationalQueryState } from "@/components/operational/OperationalQueryState";
import { OperationalSearchField } from "@/components/operational/OperationalSearchField";
import { OperationalSelect } from "@/components/operational/OperationalSelect";
import { ProductLedger } from "@/components/products/ProductLedger";
import {
  ProductRequestReviewDrawer,
  type ProductRequestReviewMode,
} from "@/components/products/ProductRequestReviewDrawer";
import { ProductRequestsLedger } from "@/components/products/ProductRequestsLedger";
import { SupplierProductRequestDrawer } from "@/components/products/SupplierProductRequestDrawer";
import { getUserFacingErrorMessage } from "@/api/userFacingError";
import { useCurrentUser } from "@/features/auth/useCurrentUser";
import { getOperationalCapabilities } from "@/features/auth/operationalCapabilities";
import {
  useCreateProduct,
  useDeleteProduct,
  useProductPage,
  useUpdateProduct,
} from "@/features/product/hooks";
import type {
  Product,
  ProductCategory,
  ProductUnit,
} from "@/features/product/types";
import { normalizeProductUnit } from "@/features/product/normalization";
import {
  useApproveCreateProductRequest,
  useApproveMapProductRequest,
  useCreateSupplierProductRequest,
  usePendingProductRequestCount,
  usePendingProductRequestById,
  usePendingProductRequestsPage,
  useRejectProductRequest,
  useSupplierProductRequestCount,
  useSupplierProductRequestsPage,
} from "@/features/productRequest/hooks";
import type {
  ProductRequest,
  ProductRequestStatus,
} from "@/features/productRequest/types";
import {
  useAssociateSupplierCatalogProduct,
  useRemoveSupplierCatalogProduct,
  useSupplierCatalogPage,
} from "@/features/supplierCatalog/hooks";
import { useDebouncedValue } from "@/hooks/useDebouncedValue";
import { readProdutosReviewSearch } from "./produtosReviewSearch";
import "./ProdutosPage.css";

const PRODUCT_PAGE_SIZE = 20;
const REQUEST_PAGE_SIZE = 10;

const CATEGORY_LABELS: Record<ProductCategory, string> = {
  LEGUMES: "Legumes",
  FRUTAS: "Frutas",
  VERDURAS: "Verduras",
  GRAOS: "Grãos",
  TEMPEROS: "Temperos",
  OVOS: "Ovos",
  OUTROS: "Diversos",
};

const UNIT_LABELS: Record<ProductUnit, string> = {
  kg: "Kg",
  cx_kg: "Cx/Kg",
  cx_un: "Cx/Un",
  bdj: "Bandeja",
  un: "Un",
  sc_kg: "Sc/Kg",
};

const REQUEST_STATUS_LABELS: Record<ProductRequestStatus, string> = {
  pending: "Pendente",
  approved_created: "Aprovado e criado",
  approved_mapped: "Vinculado",
  rejected: "Rejeitado",
};

const CATEGORY_FILTER_OPTIONS = [
  { value: "", label: "Todas as categorias" },
  ...(Object.entries(CATEGORY_LABELS) as [ProductCategory, string][]).map(
    ([value, label]) => ({ value, label }),
  ),
];

const REQUEST_STATUS_FILTER_OPTIONS = [
  { value: "", label: "Todos os status" },
  ...(
    Object.entries(REQUEST_STATUS_LABELS) as [ProductRequestStatus, string][]
  ).map(([value, label]) => ({ value, label })),
];

type ActiveTab = "products" | "requests";
type SupplierMode = "mine" | "available";
type DrawerMode = "create" | "edit";

type CatalogState = {
  offset: number;
  search: string;
  category?: ProductCategory;
};

type RequestState = {
  offset: number;
  search: string;
  status?: ProductRequestStatus;
  date?: string;
};

type DrawerState = {
  mode: DrawerMode;
  product?: Product;
};

type ToastState = {
  tone: FloatingToastTone;
  title: string;
  message?: string;
};

const EMPTY_CATALOG: CatalogState = { offset: 0, search: "" };
const EMPTY_REQUESTS: RequestState = { offset: 0, search: "" };

export function getValidOffset(
  total: number,
  offset: number,
  pageSize: number,
) {
  if (total <= 0) return 0;
  return Math.min(offset, Math.floor((total - 1) / pageSize) * pageSize);
}

function ProductTabs({
  activeTab,
  isSupplier,
  showRequests,
  requestCount,
  onChange,
}: {
  activeTab: ActiveTab;
  isSupplier: boolean;
  showRequests: boolean;
  requestCount: number;
  onChange: (tab: ActiveTab) => void;
}) {
  const productsTabRef = useRef<HTMLButtonElement | null>(null);
  const requestsTabRef = useRef<HTMLButtonElement | null>(null);
  const productsLabel = isSupplier ? "Minha lista" : "Produtos";
  const countLabel = isSupplier
    ? `${requestCount}`
    : `${requestCount} ${requestCount === 1 ? "pendente" : "pendentes"}`;

  function handleTabKeyDown(event: React.KeyboardEvent<HTMLButtonElement>) {
    let nextTab: ActiveTab | undefined;
    if (event.key === "Home") nextTab = "products";
    else if (event.key === "End" && showRequests) nextTab = "requests";
    else if (event.key === "ArrowLeft" || event.key === "ArrowRight") {
      nextTab =
        showRequests && activeTab === "products" ? "requests" : "products";
    }
    if (!nextTab) return;

    event.preventDefault();
    onChange(nextTab);
    (nextTab === "products" ? productsTabRef : requestsTabRef).current?.focus();
  }

  return (
    <div
      aria-label="Seções de produtos"
      className="produtos-tabs"
      role="tablist"
    >
      <button
        aria-controls={
          activeTab === "products" ? "produtos-products-panel" : undefined
        }
        aria-selected={activeTab === "products"}
        className="produtos-tab"
        id="produtos-products-tab"
        onKeyDown={handleTabKeyDown}
        onClick={() => onChange("products")}
        ref={productsTabRef}
        role="tab"
        tabIndex={activeTab === "products" ? 0 : -1}
        type="button"
      >
        {productsLabel}
      </button>
      {showRequests ? (
        <button
          aria-controls={
            activeTab === "requests" ? "produtos-requests-panel" : undefined
          }
          aria-label={`Solicitações, ${countLabel}`}
          aria-selected={activeTab === "requests"}
          className="produtos-tab"
          id="produtos-requests-tab"
          onKeyDown={handleTabKeyDown}
          onClick={() => onChange("requests")}
          ref={requestsTabRef}
          role="tab"
          tabIndex={activeTab === "requests" ? 0 : -1}
          type="button"
        >
          Solicitações
          <span aria-hidden="true" className="produtos-tab-count">
            {requestCount}
          </span>
        </button>
      ) : null}
    </div>
  );
}

function CatalogFilterFields({
  category,
  onCategoryChange,
}: {
  category: "" | ProductCategory;
  onCategoryChange: (category: "" | ProductCategory) => void;
}) {
  return (
    <div className="produtos-filter-field">
      <span>Categoria</span>
      <OperationalSelect
        ariaLabel="Categoria"
        menuLayer="overlay"
        onChange={(value) => onCategoryChange(value as "" | ProductCategory)}
        options={CATEGORY_FILTER_OPTIONS}
        value={category}
      />
    </div>
  );
}

function RequestFilterFields({
  date,
  status,
  showStatus,
  onDateChange,
  onStatusChange,
}: {
  date: string;
  status: "" | ProductRequestStatus;
  showStatus: boolean;
  onDateChange: (date: string) => void;
  onStatusChange: (status: "" | ProductRequestStatus) => void;
}) {
  return (
    <>
      {showStatus ? (
        <div className="produtos-filter-field">
          <span>Status</span>
          <OperationalSelect
            ariaLabel="Status"
            menuLayer="overlay"
            onChange={(value) =>
              onStatusChange(value as "" | ProductRequestStatus)
            }
            options={REQUEST_STATUS_FILTER_OPTIONS}
            value={status}
          />
        </div>
      ) : null}
      <label className="produtos-filter-field">
        <span>Data</span>
        <input
          aria-label="Data"
          onInput={(event) => onDateChange(event.currentTarget.value)}
          type="date"
          value={date}
        />
      </label>
    </>
  );
}

function LedgerSkeleton({ label }: { label: string }) {
  return (
    <div aria-label={label} className="produtos-skeleton" role="status">
      {Array.from({ length: 5 }, (_, index) => (
        <span key={index} />
      ))}
    </div>
  );
}

function SupplierRequestDetail({
  request,
  onClose,
}: {
  request: ProductRequest | null;
  onClose: () => void;
}) {
  if (!request) return null;

  return (
    <AdminDrawer
      open
      onClose={onClose}
      title="Detalhes da solicitação"
      visualTreatment="operational-sheet"
    >
      <dl className="produtos-request-detail">
        <div>
          <dt>Produto</dt>
          <dd>{request.requested_name}</dd>
        </div>
        <div>
          <dt>Status</dt>
          <dd>{REQUEST_STATUS_LABELS[request.status]}</dd>
        </div>
        {request.note ? (
          <div>
            <dt>Observação</dt>
            <dd>{request.note}</dd>
          </div>
        ) : null}
        {request.decision_note ? (
          <div>
            <dt>Retorno</dt>
            <dd>{request.decision_note}</dd>
          </div>
        ) : null}
      </dl>
    </AdminDrawer>
  );
}

export function ProdutosPage() {
  const navigate = useNavigate();
  const locationSearch = useRouterState({
    select: (state) => state.location.search,
  });
  const reviewSearch = readProdutosReviewSearch(
    (locationSearch as Record<string, unknown>) ?? {},
  );
  const { data: currentUser } = useCurrentUser();
  const roleResolved = currentUser !== undefined;
  const isSupplier = currentUser?.role === "supplier";
  const { canAudit, canDelete, canManageCatalog } =
    getOperationalCapabilities(currentUser);

  const [activeTab, setActiveTab] = useState<ActiveTab>(() =>
    reviewSearch.reviewRequestId && canAudit ? "requests" : "products",
  );
  const [supplierMode, setSupplierMode] = useState<SupplierMode>("mine");
  const [adminCatalog, setAdminCatalog] = useState<CatalogState>(EMPTY_CATALOG);
  const [mineCatalog, setMineCatalog] = useState<CatalogState>(EMPTY_CATALOG);
  const [availableCatalog, setAvailableCatalog] =
    useState<CatalogState>(EMPTY_CATALOG);
  const [requestState, setRequestState] =
    useState<RequestState>(EMPTY_REQUESTS);
  const [reviewingRequestId, setReviewingRequestId] = useState<string | null>(
    null,
  );
  const [reviewMode, setReviewMode] =
    useState<ProductRequestReviewMode>("create");
  const [requestName, setRequestName] = useState("");
  const [requestCategory, setRequestCategory] =
    useState<ProductCategory>("LEGUMES");
  const [requestUnit, setRequestUnit] = useState<ProductUnit>("kg");
  const [mappedProductId, setMappedProductId] = useState("");
  const [mappingSearchQuery, setMappingSearchQuery] = useState("");
  const [rejectionNote, setRejectionNote] = useState("");
  const [reviewError, setReviewError] = useState("");
  const handledReviewRequestId = useRef<string | null>(null);

  const debouncedAdminSearch = useDebouncedValue(
    adminCatalog.search.trim(),
    250,
  );
  const debouncedMineSearch = useDebouncedValue(mineCatalog.search.trim(), 250);
  const debouncedAvailableSearch = useDebouncedValue(
    availableCatalog.search.trim(),
    250,
  );
  const debouncedRequestSearch = useDebouncedValue(
    requestState.search.trim(),
    250,
  );
  const normalizedMappingSearchQuery = mappingSearchQuery.trim();
  const debouncedMappingSearch = useDebouncedValue(
    normalizedMappingSearchQuery,
    250,
  );
  const mappingSearchSettled =
    normalizedMappingSearchQuery === debouncedMappingSearch;
  const mappingProductPage = useProductPage(
    {
      limit: PRODUCT_PAGE_SIZE,
      offset: 0,
      search: normalizedMappingSearchQuery ? debouncedMappingSearch : "",
    },
    roleResolved &&
      canAudit &&
      !isSupplier &&
      reviewingRequestId !== null &&
      reviewMode === "map",
  );

  const adminPage = useProductPage(
    {
      limit: PRODUCT_PAGE_SIZE,
      offset: adminCatalog.offset,
      search: debouncedAdminSearch,
      ...(adminCatalog.category ? { category: adminCatalog.category } : {}),
    },
    roleResolved && canManageCatalog && !isSupplier && activeTab === "products",
  );
  const minePage = useSupplierCatalogPage(
    {
      mode: "mine",
      limit: PRODUCT_PAGE_SIZE,
      offset: mineCatalog.offset,
      search: debouncedMineSearch,
      ...(mineCatalog.category ? { category: mineCatalog.category } : {}),
    },
    roleResolved &&
      isSupplier &&
      activeTab === "products" &&
      supplierMode === "mine",
  );
  const availablePage = useSupplierCatalogPage(
    {
      mode: "available",
      limit: PRODUCT_PAGE_SIZE,
      offset: availableCatalog.offset,
      search: debouncedAvailableSearch,
      ...(availableCatalog.category
        ? { category: availableCatalog.category }
        : {}),
    },
    roleResolved &&
      isSupplier &&
      activeTab === "products" &&
      supplierMode === "available",
  );
  const pendingRequestsPage = usePendingProductRequestsPage(
    {
      limit: REQUEST_PAGE_SIZE,
      offset: requestState.offset,
      search: debouncedRequestSearch,
      ...(requestState.date ? { date: requestState.date } : {}),
    },
    roleResolved && canAudit && !isSupplier && activeTab === "requests",
  );
  const supplierRequestsPage = useSupplierProductRequestsPage(
    {
      limit: REQUEST_PAGE_SIZE,
      offset: requestState.offset,
      search: debouncedRequestSearch,
      ...(requestState.status ? { status: requestState.status } : {}),
      ...(requestState.date ? { date: requestState.date } : {}),
    },
    roleResolved && isSupplier && activeTab === "requests",
  );
  const pendingRequestCount = usePendingProductRequestCount(
    roleResolved && canAudit && !isSupplier && activeTab === "products",
  );
  const supplierRequestCount = useSupplierProductRequestCount(
    roleResolved && isSupplier && activeTab === "products",
  );
  const targetReviewRequest = usePendingProductRequestById(
    reviewSearch.reviewRequestId,
    roleResolved &&
      canAudit &&
      !isSupplier &&
      Boolean(reviewSearch.reviewRequestId),
  );

  const createMutation = useCreateProduct();
  const updateMutation = useUpdateProduct();
  const deleteMutation = useDeleteProduct();
  const associateSupplierProduct = useAssociateSupplierCatalogProduct();
  const removeSupplierProduct = useRemoveSupplierCatalogProduct();
  const createSupplierRequest = useCreateSupplierProductRequest();
  const approveCreateRequest = useApproveCreateProductRequest();
  const approveMapRequest = useApproveMapProductRequest();
  const rejectRequest = useRejectProductRequest();

  const [filtersOpen, setFiltersOpen] = useState(false);
  const [draftCategory, setDraftCategory] = useState<"" | ProductCategory>("");
  const [draftStatus, setDraftStatus] = useState<"" | ProductRequestStatus>("");
  const [draftDate, setDraftDate] = useState("");
  const [drawer, setDrawer] = useState<DrawerState | null>(null);
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [requestDrawerOpen, setRequestDrawerOpen] = useState(false);
  const [requestNameInput, setRequestNameInput] = useState("");
  const [requestNote, setRequestNote] = useState("");
  const [supplierRequestError, setSupplierRequestError] = useState("");
  const [supplierRequestDetail, setSupplierRequestDetail] =
    useState<ProductRequest | null>(null);
  const [pendingProductId, setPendingProductId] = useState<string>();
  const [toast, setToast] = useState<ToastState | null>(null);
  const [listErrorToast, setListErrorToast] = useState<ToastState | null>(null);
  const lastListErrorEvent = useRef<{
    error: unknown;
    queryIdentity: string;
  } | null>(null);

  const [name, setName] = useState("");
  const [category, setCategory] = useState<ProductCategory>("LEGUMES");
  const [defaultUnit, setDefaultUnit] = useState<ProductUnit>("kg");
  const [productDrawerError, setProductDrawerError] = useState("");

  const knownRequestCount = Math.max(
    0,
    Number(
      isSupplier
        ? (supplierRequestCount.data ?? 0)
        : (pendingRequestCount.data ?? 0),
    ),
  );
  const activeCatalogState = isSupplier
    ? supplierMode === "mine"
      ? mineCatalog
      : availableCatalog
    : adminCatalog;
  const activeCatalogPage = isSupplier
    ? supplierMode === "mine"
      ? minePage
      : availablePage
    : adminPage;
  const activeRequestPage = isSupplier
    ? supplierRequestsPage
    : pendingRequestsPage;
  const requestCount = Math.max(
    0,
    Number(
      activeTab === "requests" && activeRequestPage.data
        ? activeRequestPage.data.total
        : knownRequestCount,
    ),
  );
  const activePage =
    activeTab === "products" ? activeCatalogPage : activeRequestPage;
  const activeItems = activePage.data?.items ?? [];
  const activeTotal = activePage.data?.total ?? 0;
  const activeOffset =
    activeTab === "products" ? activeCatalogState.offset : requestState.offset;
  const activePageSize =
    activeTab === "products" ? PRODUCT_PAGE_SIZE : REQUEST_PAGE_SIZE;
  const activePageCount = Math.max(1, Math.ceil(activeTotal / activePageSize));
  const isUpdating = activePage.isFetching && activeItems.length > 0;
  const hasActiveFilters =
    activeTab === "products"
      ? Boolean(activeCatalogState.search.trim() || activeCatalogState.category)
      : Boolean(
          requestState.search.trim() ||
          requestState.status ||
          requestState.date,
        );
  const activeFilterCount =
    activeTab === "products"
      ? Number(Boolean(activeCatalogState.category))
      : Number(Boolean(isSupplier && requestState.status)) +
        Number(Boolean(requestState.date));
  const activeRequests = activeRequestPage.data?.items ?? [];
  const selectedRequest =
    targetReviewRequest.data?.id === reviewingRequestId
      ? targetReviewRequest.data
      : activeRequests.find((request) => request.id === reviewingRequestId);
  const activeQueryIdentity = JSON.stringify({
    tab: activeTab,
    scope: isSupplier ? `supplier:${supplierMode}` : "admin",
    offset: activeOffset,
    search:
      activeTab === "products"
        ? isSupplier
          ? supplierMode === "mine"
            ? debouncedMineSearch
            : debouncedAvailableSearch
          : debouncedAdminSearch
        : debouncedRequestSearch,
    category:
      activeTab === "products" ? (activeCatalogState.category ?? "") : "",
    status:
      activeTab === "requests" && isSupplier ? (requestState.status ?? "") : "",
    date: activeTab === "requests" ? (requestState.date ?? "") : "",
  });
  const queryErrorMessage = activePage.isError
    ? getUserFacingErrorMessage(
        activePage.error,
        activeTab === "products"
          ? "Não foi possível carregar os produtos. Tente novamente."
          : "Não foi possível carregar as solicitações. Tente novamente.",
      )
    : "";
  const queryErrorTitle =
    activeTab === "products"
      ? "Não foi possível carregar os produtos"
      : "Não foi possível carregar as solicitações";

  useEffect(() => {
    if (!toast) return;
    const timeout = window.setTimeout(() => setToast(null), 3200);
    return () => window.clearTimeout(timeout);
  }, [toast]);

  useEffect(() => {
    if (!roleResolved || !activePage.isError) {
      lastListErrorEvent.current = null;
      setListErrorToast(null);
      return;
    }

    const lastEvent = lastListErrorEvent.current;
    if (
      lastEvent?.error === activePage.error &&
      lastEvent.queryIdentity === activeQueryIdentity
    ) {
      return;
    }

    lastListErrorEvent.current = {
      error: activePage.error,
      queryIdentity: activeQueryIdentity,
    };
    setListErrorToast({
      tone: "error",
      title: queryErrorTitle,
      message: queryErrorMessage,
    });
  }, [
    activePage.error,
    activePage.isError,
    activeQueryIdentity,
    queryErrorMessage,
    queryErrorTitle,
    roleResolved,
  ]);

  useEffect(() => {
    if (adminPage.data && !adminPage.isPlaceholderData) {
      const valid = getValidOffset(
        adminPage.data.total,
        adminCatalog.offset,
        PRODUCT_PAGE_SIZE,
      );
      if (valid !== adminCatalog.offset) {
        setAdminCatalog((state) => ({ ...state, offset: valid }));
      }
    }
  }, [adminCatalog.offset, adminPage.data, adminPage.isPlaceholderData]);

  useEffect(() => {
    if (minePage.data && !minePage.isPlaceholderData) {
      const valid = getValidOffset(
        minePage.data.total,
        mineCatalog.offset,
        PRODUCT_PAGE_SIZE,
      );
      if (valid !== mineCatalog.offset) {
        setMineCatalog((state) => ({ ...state, offset: valid }));
      }
    }
  }, [mineCatalog.offset, minePage.data, minePage.isPlaceholderData]);

  useEffect(() => {
    if (availablePage.data && !availablePage.isPlaceholderData) {
      const valid = getValidOffset(
        availablePage.data.total,
        availableCatalog.offset,
        PRODUCT_PAGE_SIZE,
      );
      if (valid !== availableCatalog.offset) {
        setAvailableCatalog((state) => ({ ...state, offset: valid }));
      }
    }
  }, [
    availableCatalog.offset,
    availablePage.data,
    availablePage.isPlaceholderData,
  ]);

  useEffect(() => {
    if (activeRequestPage.data && !activeRequestPage.isPlaceholderData) {
      const valid = getValidOffset(
        activeRequestPage.data.total,
        requestState.offset,
        REQUEST_PAGE_SIZE,
      );
      if (valid !== requestState.offset) {
        setRequestState((state) => ({ ...state, offset: valid }));
      }
    }
  }, [
    activeRequestPage.data,
    activeRequestPage.isPlaceholderData,
    requestState.offset,
  ]);

  useEffect(() => {
    const requestId = reviewSearch.reviewRequestId?.trim();
    if (!requestId) {
      handledReviewRequestId.current = null;
      return;
    }
    if (
      !roleResolved ||
      isSupplier ||
      targetReviewRequest.isLoading ||
      targetReviewRequest.isFetching ||
      handledReviewRequestId.current === requestId
    ) {
      return;
    }

    handledReviewRequestId.current = requestId;
    if (targetReviewRequest.isError) {
      setToast({
        tone: "error",
        title: "Não foi possível abrir a solicitação",
        message: getUserFacingErrorMessage(
          targetReviewRequest.error,
          "Não foi possível abrir a solicitação. Tente novamente.",
        ),
      });
    } else if (targetReviewRequest.data) {
      openRequestReview(
        targetReviewRequest.data,
        reviewSearch.reviewMode ?? "create",
      );
    } else {
      setToast({
        tone: "warning",
        title: "Solicitação não encontrada",
        message: "Ela pode já ter sido analisada.",
      });
    }
    navigate({ to: "/mais/produtos", search: {}, replace: true });
  }, [
    isSupplier,
    navigate,
    roleResolved,
    reviewSearch.reviewMode,
    reviewSearch.reviewRequestId,
    targetReviewRequest.data,
    targetReviewRequest.error,
    targetReviewRequest.isError,
    targetReviewRequest.isFetching,
    targetReviewRequest.isLoading,
  ]);

  function setActiveCatalog(next: (state: CatalogState) => CatalogState) {
    if (!isSupplier) setAdminCatalog(next);
    else if (supplierMode === "mine") setMineCatalog(next);
    else setAvailableCatalog(next);
  }

  function changeTab(tab: ActiveTab) {
    setActiveTab(tab);
    setFiltersOpen(false);
  }

  function handleSearchChange(value: string) {
    if (activeTab === "products") {
      setActiveCatalog((state) => ({ ...state, search: value, offset: 0 }));
    } else {
      setRequestState((state) => ({ ...state, search: value, offset: 0 }));
    }
  }

  function handleFilterOpenChange(open: boolean) {
    if (open) {
      if (activeTab === "products") {
        setDraftCategory(activeCatalogState.category ?? "");
      } else {
        setDraftStatus(requestState.status ?? "");
        setDraftDate(requestState.date ?? "");
      }
    }
    setFiltersOpen(open);
  }

  function handleApplyFilters() {
    if (activeTab === "products") {
      setActiveCatalog((state) => ({
        ...state,
        offset: 0,
        ...(draftCategory
          ? { category: draftCategory }
          : { category: undefined }),
      }));
    } else {
      setRequestState((state) => ({
        ...state,
        offset: 0,
        ...(isSupplier && draftStatus
          ? { status: draftStatus }
          : { status: undefined }),
        ...(draftDate ? { date: draftDate } : { date: undefined }),
      }));
    }
    setFiltersOpen(false);
  }

  function handleClearFilters() {
    setDraftCategory("");
    setDraftStatus("");
    setDraftDate("");
    if (activeTab === "products") {
      setActiveCatalog((state) => ({
        ...state,
        offset: 0,
        category: undefined,
      }));
    } else {
      setRequestState((state) => ({
        ...state,
        offset: 0,
        status: undefined,
        date: undefined,
      }));
    }
    setFiltersOpen(false);
  }

  function handlePageChange(page: number) {
    const offset = page * activePageSize;
    if (activeTab === "products") {
      setActiveCatalog((state) => ({ ...state, offset }));
    } else {
      setRequestState((state) => ({ ...state, offset }));
    }
  }

  function openCreate() {
    if (!canManageCatalog) return;
    setName("");
    setCategory("LEGUMES");
    setDefaultUnit("kg");
    setConfirmDelete(false);
    setProductDrawerError("");
    setDrawer({ mode: "create" });
  }

  function openEdit(product: Product) {
    if (!canManageCatalog) return;
    setName(product.name);
    setCategory(product.category);
    setDefaultUnit(normalizeProductUnit(product.default_unit));
    setConfirmDelete(false);
    setProductDrawerError("");
    setDrawer({ mode: "edit", product });
  }

  function closeDrawer() {
    setDrawer(null);
    setConfirmDelete(false);
    setProductDrawerError("");
  }

  async function handleSubmit() {
    if (!canManageCatalog || isProductMutationPending || !name.trim()) return;
    setProductDrawerError("");
    try {
      if (drawer?.mode === "create") {
        await createMutation.mutateAsync({
          name: name.trim(),
          category,
          default_unit: defaultUnit,
        });
      } else if (drawer?.mode === "edit" && drawer.product) {
        await updateMutation.mutateAsync({
          id: drawer.product.id,
          payload: { name: name.trim(), category, default_unit: defaultUnit },
        });
      }
      setToast({ tone: "success", title: "Produto salvo" });
      closeDrawer();
    } catch (error) {
      const message = getUserFacingErrorMessage(
        error,
        "Não foi possível salvar o produto. Tente novamente.",
      );
      setProductDrawerError(message);
      setToast({
        tone: "error",
        title: "Não foi possível salvar o produto",
        message,
      });
    }
  }

  async function handleDelete() {
    if (!canDelete || isProductMutationPending || !drawer?.product) return;
    setProductDrawerError("");
    try {
      await deleteMutation.mutateAsync(drawer.product.id);
      setToast({ tone: "success", title: "Produto excluído" });
      closeDrawer();
    } catch (error) {
      const message = getUserFacingErrorMessage(
        error,
        "Não foi possível excluir o produto. Tente novamente.",
      );
      setProductDrawerError(message);
      setToast({
        tone: "error",
        title: "Não foi possível excluir o produto",
        message,
      });
    }
  }

  async function handleSupplierAssociation(product: Product) {
    const removing = supplierMode === "mine";
    setPendingProductId(product.id);
    try {
      if (removing) await removeSupplierProduct.mutateAsync(product.id);
      else
        await associateSupplierProduct.mutateAsync({ product_id: product.id });
      setToast({
        tone: "success",
        title: removing
          ? "Produto removido da lista"
          : "Produto adicionado à lista",
      });
    } catch (error) {
      setToast({
        tone: "error",
        title: removing
          ? "Não foi possível remover o produto"
          : "Não foi possível adicionar o produto",
        message: getUserFacingErrorMessage(
          error,
          removing
            ? "Não foi possível remover o produto. Tente novamente."
            : "Não foi possível adicionar o produto. Tente novamente.",
        ),
      });
    } finally {
      setPendingProductId(undefined);
    }
  }

  function openSupplierRequestDrawer() {
    setRequestNameInput(requestState.search.trim());
    setRequestNote("");
    setSupplierRequestError("");
    setRequestDrawerOpen(true);
  }

  async function handleSupplierRequestSubmit() {
    if (isSupplierRequestPending) return;
    setSupplierRequestError("");
    if (!requestNameInput.trim()) {
      setSupplierRequestError("Informe o nome do produto.");
      setToast({ tone: "warning", title: "Informe o nome do produto" });
      return;
    }
    try {
      await createSupplierRequest.mutateAsync({
        requested_name: requestNameInput.trim(),
        note: requestNote.trim() || undefined,
      });
      setToast({ tone: "success", title: "Solicitação enviada" });
      setRequestDrawerOpen(false);
      setRequestNote("");
    } catch (error) {
      const message = getUserFacingErrorMessage(
        error,
        "Não foi possível enviar a solicitação. Tente novamente.",
      );
      setSupplierRequestError(message);
      setToast({
        tone: "error",
        title: "Não foi possível enviar a solicitação",
        message,
      });
    }
  }

  function openRequestReview(
    request: ProductRequest,
    mode: ProductRequestReviewMode,
  ) {
    setReviewingRequestId(request.id);
    setReviewMode(mode);
    setRequestName(request.requested_name);
    setRequestCategory("LEGUMES");
    setRequestUnit("kg");
    setMappingSearchQuery("");
    setMappedProductId("");
    setRejectionNote("");
    setReviewError("");
  }

  function closeRequestReview() {
    setReviewingRequestId(null);
    setReviewError("");
    setMappingSearchQuery("");
    setMappedProductId("");
    setRejectionNote("");
  }

  function handleMappingSearchQueryChange(value: string) {
    if (value !== mappingSearchQuery) setMappedProductId("");
    setMappingSearchQuery(value);
    setReviewError("");
  }

  function handleRequestNameChange(value: string) {
    setRequestName(value);
    setReviewError("");
  }

  function handleRequestCategoryChange(value: ProductCategory) {
    setRequestCategory(value);
    setReviewError("");
  }

  function handleRequestUnitChange(value: ProductUnit) {
    setRequestUnit(value);
    setReviewError("");
  }

  function handleMappedProductIdChange(value: string) {
    setMappedProductId(value);
    setReviewError("");
  }

  function handleRejectionNoteChange(value: string) {
    setRejectionNote(value);
    setReviewError("");
  }

  function handleListErrorRetry() {
    setListErrorToast(null);
    void activePage.refetch();
  }

  async function handleRequestReviewSubmit() {
    if (isRequestMutationPending || !reviewingRequestId) return;
    setReviewError("");
    try {
      if (reviewMode === "create") {
        await approveCreateRequest.mutateAsync({
          id: reviewingRequestId,
          payload: {
            name: requestName.trim(),
            category: requestCategory,
            default_unit: requestUnit,
          },
        });
      } else if (reviewMode === "map") {
        const selectedProductIsVisible =
          mappingSearchSettled &&
          !mappingProductPage.isPlaceholderData &&
          mappingProductPage.data?.items.some(
            (product) => product.id === mappedProductId,
          );
        if (!mappedProductId || !selectedProductIsVisible) {
          setReviewError("Selecione um produto existente para vincular.");
          return;
        }
        await approveMapRequest.mutateAsync({
          id: reviewingRequestId,
          payload: { product_id: mappedProductId },
        });
      } else {
        await rejectRequest.mutateAsync({
          id: reviewingRequestId,
          payload: { decision_note: rejectionNote.trim() || undefined },
        });
      }
      setToast({ tone: "success", title: "Solicitação revisada" });
      closeRequestReview();
    } catch (error) {
      const message = getUserFacingErrorMessage(
        error,
        "Não foi possível revisar a solicitação. Tente novamente.",
      );
      setReviewError(message);
      setToast({
        tone: "error",
        title: "Não foi possível revisar a solicitação",
        message,
      });
    }
  }

  const isProductMutationPending =
    createMutation.isPending ||
    updateMutation.isPending ||
    deleteMutation.isPending;
  const isRequestMutationPending =
    approveCreateRequest.isPending ||
    approveMapRequest.isPending ||
    rejectRequest.isPending;
  const isSupplierRequestPending = createSupplierRequest.isPending;
  const activeSearch =
    activeTab === "products" ? activeCatalogState.search : requestState.search;
  const mappingErrorMessage = mappingProductPage.isError
    ? getUserFacingErrorMessage(
        mappingProductPage.error,
        "Não foi possível buscar os produtos. Tente novamente.",
      )
    : "";

  if (!roleResolved) {
    return (
      <AdminPageShell
        breadcrumb={
          <AdminBreadcrumb
            items={[
              { label: "Mais", onClick: () => navigate({ to: "/mais" }) },
              { label: "Produtos" },
            ]}
          />
        }
        icon={Package}
        size="standard"
        showIcon={false}
        title="PRODUTOS"
      >
        <main className="produtos-page">
          <LedgerSkeleton label="Carregando produtos" />
        </main>
      </AdminPageShell>
    );
  }

  function renderPageAction() {
    if (canManageCatalog && !isSupplier && activeTab === "products") {
      return (
        <button
          className="produtos-primary-action"
          onClick={openCreate}
          style={adminAddButtonStyle}
          type="button"
        >
          <Plus aria-hidden="true" size={16} />
          Adicionar produto
        </button>
      );
    }
    return undefined;
  }

  return (
    <AdminPageShell
      action={renderPageAction()}
      breadcrumb={
        <AdminBreadcrumb
          items={[
            { label: "Mais", onClick: () => navigate({ to: "/mais" }) },
            { label: "Produtos" },
          ]}
        />
      }
      icon={Package}
      size="standard"
      showIcon={false}
      title="PRODUTOS"
    >
      <main className="produtos-page">
        {supplierMode === "mine" || activeTab === "requests" || !isSupplier ? (
          <ProductTabs
            activeTab={activeTab}
            isSupplier={isSupplier}
            showRequests={isSupplier || canAudit}
            onChange={changeTab}
            requestCount={requestCount}
          />
        ) : null}

        {isSupplier &&
        activeTab === "products" &&
        supplierMode === "available" ? (
          <header className="produtos-add-header">
            <button
              aria-label="Voltar para minha lista"
              className="produtos-back"
              onClick={() => {
                setFiltersOpen(false);
                setSupplierMode("mine");
              }}
              type="button"
            >
              <ArrowLeft aria-hidden="true" size={18} />
              Voltar
            </button>
            <h2>Adicionar produtos</h2>
          </header>
        ) : null}

        <section
          aria-labelledby={
            activeTab === "products"
              ? "produtos-products-tab"
              : "produtos-requests-tab"
          }
          className="produtos-workspace"
          id={
            activeTab === "products"
              ? "produtos-products-panel"
              : "produtos-requests-panel"
          }
          role="tabpanel"
        >
          {isSupplier &&
          ((activeTab === "products" && supplierMode === "mine") ||
            activeTab === "requests") ? (
            <div className="produtos-workspace-action">
              <button
                className="produtos-primary-action"
                onClick={
                  activeTab === "requests"
                    ? openSupplierRequestDrawer
                    : () => {
                        setFiltersOpen(false);
                        setSupplierMode("available");
                      }
                }
                style={adminAddButtonStyle}
                type="button"
              >
                <Plus aria-hidden="true" size={16} />
                {activeTab === "requests"
                  ? "Nova solicitação"
                  : "Adicionar produtos"}
              </button>
            </div>
          ) : null}

          {reviewSearch.reviewRequestId &&
          (targetReviewRequest.isLoading || targetReviewRequest.isFetching) ? (
            <p className="produtos-deep-link-status" role="status">
              Carregando solicitação…
            </p>
          ) : null}

          <div className="produtos-toolbar">
            <div className="produtos-search">
              <OperationalSearchField
                ariaLabel={
                  activeTab === "requests"
                    ? "Buscar solicitações"
                    : isSupplier && supplierMode === "mine"
                      ? "Buscar minha lista"
                      : "Buscar produtos"
                }
                onChange={handleSearchChange}
                placeholder={
                  activeTab === "requests"
                    ? "Buscar solicitação"
                    : "Buscar produto"
                }
                value={activeSearch}
              />
            </div>
            <OperationalFilterSurface
              activeCount={activeFilterCount}
              onApply={handleApplyFilters}
              onClear={handleClearFilters}
              onOpenChange={handleFilterOpenChange}
              open={filtersOpen}
            >
              {activeTab === "products" ? (
                <CatalogFilterFields
                  category={draftCategory}
                  onCategoryChange={setDraftCategory}
                />
              ) : (
                <RequestFilterFields
                  date={draftDate}
                  onDateChange={setDraftDate}
                  onStatusChange={setDraftStatus}
                  showStatus={isSupplier}
                  status={draftStatus}
                />
              )}
            </OperationalFilterSurface>
            <span
              aria-label={isUpdating ? "Atualizando resultados" : undefined}
              aria-live="polite"
              className="produtos-update-status"
              data-visible={isUpdating ? "true" : undefined}
              role={isUpdating ? "status" : undefined}
            >
              {isUpdating ? "Atualizando…" : ""}
            </span>
          </div>

          <OperationalQueryState
            error={activeItems.length > 0 ? null : activePage.error}
            hasData={activeItems.length > 0}
            isFetching={activePage.isFetching}
            isFiltered={hasActiveFilters}
            isLoading={activePage.isLoading}
            onRetry={() => void activePage.refetch()}
            renderEmpty={({ filtered }) => (
              <p className="produtos-empty">
                {activeTab === "products"
                  ? filtered
                    ? "Nenhum produto encontrado."
                    : supplierMode === "available" && isSupplier
                      ? "Nenhum produto disponível para adicionar."
                      : isSupplier && supplierMode === "mine"
                        ? "Nenhum produto na sua lista."
                        : "Nenhum produto cadastrado."
                  : filtered
                    ? "Nenhuma solicitação encontrada."
                    : "Nenhuma solicitação ainda."}
              </p>
            )}
            renderSkeleton={() => (
              <LedgerSkeleton
                label={
                  activeTab === "products"
                    ? "Carregando produtos"
                    : "Carregando solicitações"
                }
              />
            )}
          >
            <div
              aria-busy={isUpdating}
              className="operational-directory__updating produtos-ledger-shell"
            >
              {activeTab === "products" ? (
                <ProductLedger
                  action={
                    canManageCatalog && !isSupplier
                      ? "edit"
                      : supplierMode === "mine"
                        ? "remove"
                        : "add"
                  }
                  onAction={(product) => {
                    if (!isSupplier) openEdit(product);
                    else void handleSupplierAssociation(product);
                  }}
                  pendingProductId={pendingProductId}
                  products={activeItems as Product[]}
                />
              ) : (
                <ProductRequestsLedger
                  onReview={
                    isSupplier
                      ? undefined
                      : (request, mode) => openRequestReview(request, mode)
                  }
                  onView={isSupplier ? setSupplierRequestDetail : undefined}
                  requests={activeItems as ProductRequest[]}
                  variant={isSupplier ? "supplier" : "super"}
                />
              )}
            </div>
            {activePageCount > 1 ? (
              <OperationalPager
                ariaLabel={
                  activeTab === "products"
                    ? "Paginação de produtos"
                    : "Paginação de solicitações"
                }
                className="produtos-pager"
                isFetching={activePage.isFetching}
                itemLabel={activeTab === "products" ? "produto" : "solicitação"}
                itemLabelPlural={
                  activeTab === "products" ? "produtos" : "solicitações"
                }
                limit={activePageSize}
                offset={activeOffset}
                onPageChange={handlePageChange}
                page={Math.floor(activeOffset / activePageSize)}
                pageCount={activePageCount}
                pageSize={activePageSize}
                totalItems={activeTotal}
                variant="numbered"
              />
            ) : null}
          </OperationalQueryState>
        </section>

        {!isSupplier && canAudit ? (
          <ProductRequestReviewDrawer
            categoryLabels={CATEGORY_LABELS}
            error={reviewError}
            mappingSearch={{
              query: mappingSearchQuery,
              items: mappingProductPage.data?.items ?? [],
              loading:
                !mappingSearchSettled ||
                mappingProductPage.isLoading ||
                mappingProductPage.isFetching,
              disabled: !mappingSearchSettled,
              error: mappingErrorMessage,
              onQueryChange: handleMappingSearchQueryChange,
              onRetry: () => void mappingProductPage.refetch(),
            }}
            mappedProductId={mappedProductId}
            mode={reviewMode}
            onClose={closeRequestReview}
            onMappedProductIdChange={handleMappedProductIdChange}
            onRejectionNoteChange={handleRejectionNoteChange}
            onRequestCategoryChange={handleRequestCategoryChange}
            onRequestNameChange={handleRequestNameChange}
            onRequestUnitChange={handleRequestUnitChange}
            onSubmit={() => void handleRequestReviewSubmit()}
            open={reviewingRequestId !== null}
            pending={isRequestMutationPending}
            rejectionNote={rejectionNote}
            requestCategory={requestCategory}
            requestName={requestName}
            requestUnit={requestUnit}
            selectedRequest={selectedRequest}
            unitLabels={UNIT_LABELS}
          />
        ) : null}

        {!isSupplier && canManageCatalog ? (
          <AdminDrawer
            onClose={closeDrawer}
            onSubmit={handleSubmit}
            open={drawer !== null}
            submitDisabled={isProductMutationPending}
            visualTreatment="operational-sheet"
            title={
              drawer?.mode === "create" ? "Novo Produto" : "Editar Produto"
            }
          >
            <div style={adminDrawerBodyStyle}>
              <div style={adminFieldStyle}>
                <label htmlFor="product-name" style={adminLabelStyle}>
                  Nome
                </label>
                <input
                  className="product-drawer-control"
                  id="product-name"
                  onChange={(event) => {
                    setName(event.target.value);
                    setProductDrawerError("");
                  }}
                  placeholder="Ex: Tomate"
                  style={adminInputStyle}
                  value={name}
                />
              </div>
              <div style={adminFieldStyle}>
                <label htmlFor="product-category" style={adminLabelStyle}>
                  Categoria
                </label>
                <select
                  className="product-drawer-control"
                  id="product-category"
                  onChange={(event) => {
                    setCategory(event.target.value as ProductCategory);
                    setProductDrawerError("");
                  }}
                  style={adminSelectStyle}
                  value={category}
                >
                  {(
                    Object.entries(CATEGORY_LABELS) as [
                      ProductCategory,
                      string,
                    ][]
                  ).map(([value, label]) => (
                    <option key={value} value={value}>
                      {label}
                    </option>
                  ))}
                </select>
              </div>
              <div style={adminFieldStyle}>
                <label htmlFor="product-unit" style={adminLabelStyle}>
                  Unidade preferida
                </label>
                <select
                  className="product-drawer-control"
                  id="product-unit"
                  onChange={(event) => {
                    setDefaultUnit(event.target.value as ProductUnit);
                    setProductDrawerError("");
                  }}
                  style={adminSelectStyle}
                  value={defaultUnit}
                >
                  {(Object.entries(UNIT_LABELS) as [ProductUnit, string][]).map(
                    ([value, label]) => (
                      <option key={value} value={value}>
                        {label}
                      </option>
                    ),
                  )}
                </select>
              </div>
            </div>
            {productDrawerError ? (
              <p className="produtos-drawer-error" role="alert">
                {productDrawerError}
              </p>
            ) : null}
            <AdminDrawerFooter
              confirmDelete={confirmDelete}
              dangerClassName="product-drawer-action btn-danger product-drawer-danger"
              isPending={isProductMutationPending}
              mode={drawer?.mode ?? "create"}
              onConfirmDelete={canDelete ? handleDelete : undefined}
              onRequestDelete={
                canDelete ? () => setConfirmDelete(true) : undefined
              }
              onSubmit={handleSubmit}
              primaryClassName="product-drawer-action btn-primary product-drawer-primary"
              submitLabel={drawer?.mode === "create" ? "Criar" : "Salvar"}
            />
          </AdminDrawer>
        ) : null}

        <SupplierProductRequestDrawer
          error={supplierRequestError}
          note={requestNote}
          onClose={() => {
            setRequestDrawerOpen(false);
            setSupplierRequestError("");
          }}
          onNoteChange={(value) => {
            setRequestNote(value);
            setSupplierRequestError("");
          }}
          onRequestNameChange={(value) => {
            setRequestNameInput(value);
            setSupplierRequestError("");
          }}
          onSubmit={() => void handleSupplierRequestSubmit()}
          open={isSupplier && requestDrawerOpen}
          pending={isSupplierRequestPending}
          requestName={requestNameInput}
        />
        <SupplierRequestDetail
          onClose={() => setSupplierRequestDetail(null)}
          request={supplierRequestDetail}
        />

        {toast ? (
          <FloatingToast
            message={toast.message}
            onDismiss={() => setToast(null)}
            title={toast.title}
            tone={toast.tone}
          />
        ) : listErrorToast ? (
          <FloatingToast
            actionLabel="Tentar novamente"
            message={listErrorToast.message}
            onAction={handleListErrorRetry}
            onDismiss={() => setListErrorToast(null)}
            title={listErrorToast.title}
            tone={listErrorToast.tone}
          />
        ) : null}
      </main>
    </AdminPageShell>
  );
}
